import React, { useEffect } from 'react';
import { FormattedText } from '@/components/uiKit/FormattedText';
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import { FieldValues, useForm } from 'react-hook-form';
import { locations } from '@/locations';
import { useAuth } from '@/providers/AuthProvider';
import { AuthError } from 'firebase/auth';
import { TOAST_STATUS, PASSWORD_PATTERN } from '@/const';
import { mapAuthCodeToMessage } from '@/utils';
import { useToast } from '@/hooks/shared/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

type ChangePasswordFormData = {
  password: string;
  confirmPassword: string;
};

interface ChangePasswordFormProps {
  userMustChangePassword: boolean;
  oobCode: string | null;
}

export const ChangePasswordForm = ({ userMustChangePassword, oobCode }: ChangePasswordFormProps) => {
  const { toast } = useToast();
  const { changePassword, user, updatePassword, signOut } = useAuth();
  const navigate = useNavigate();
  const {
    watch,
    register,
    handleSubmit,
    getFieldState,
    getValues,
    trigger,
    formState: { errors, isSubmitting, isValid },
  } = useForm<ChangePasswordFormData>({ mode: 'onChange' });

  const passwordFieldState = getFieldState('password');
  const confirmPasswordFieldState = getFieldState('confirmPassword');

  const passwordFieldValue = getValues('password');
  const confirmPasswordFieldValue = getValues('confirmPassword');

  useEffect(() => {
    trigger('confirmPassword');
  }, [passwordFieldValue]);

  const onSubmit = async (values: FieldValues) => {
    const { password } = values;

    if (isValid) {
      if (userMustChangePassword && user) {
        try {
          await updatePassword(user, password.trim());
          localStorage.removeItem('userMustChangePassword');
          await signOut()
          navigate(locations.public.changed, { replace: true });
        } catch (error: unknown) {
          const authError = error as AuthError;

          toast({
            variant: 'destructive',
            title: mapAuthCodeToMessage(authError.code),
          });
        }
      } else if (oobCode) {
        try {
          await changePassword(oobCode, password.trim());
          navigate(locations.public.changed, { replace: true });
        } catch (error: unknown) {
          const authError = error as AuthError;

          toast({
            variant: 'destructive',
            title: mapAuthCodeToMessage(authError.code),
          });
        }
      }
    }
  };

  const isPasswordFieldInvalid = passwordFieldState.invalid && !!passwordFieldValue;
  const isConfirmPasswordFieldInvalid = confirmPasswordFieldState.invalid && !!confirmPasswordFieldValue;
  
  const getPasswordHelperColor = () => {
    if (isPasswordFieldInvalid) return 'text-red-500';
    if (passwordFieldValue) return 'text-green-600';
    return 'text-gray-600';
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="space-y-8">
        <div className="space-y-2">
          <Label htmlFor="password">
            <FormattedText label="auth.resetPassword.change.form.newPassword" />
          </Label>
          <Input
            id="password"
            type="password"
            className={cn(
              "w-full",
              isPasswordFieldInvalid && "border-red-500"
            )}
            placeholder=" "
            {...register('password', {
              required: true,
              pattern: PASSWORD_PATTERN,
            })}
          />
          <p className={cn("text-sm", getPasswordHelperColor())}>
            <FormattedText label="auth.resetPassword.change.form.passwordRestrictions" />
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword">
            <FormattedText label="auth.resetPassword.change.form.confirmPassword" />
          </Label>
          <Input
            id="confirmPassword"
            type="password"
            className={cn(
              isConfirmPasswordFieldInvalid && "border-red-500"
            )}
            placeholder=" "
            {...register('confirmPassword', {
              required: true,
              validate: (value: string) => {
                if (watch('password') !== value) {
                  return 'Your passwords do not match';
                }
              },
            })}
          />
          {errors.confirmPassword && (
            <p className="text-sm text-red-500">{errors.confirmPassword.message}</p>
          )}
        </div>
      </div>

      <div className="flex flex-col-reverse md:flex-row mt-8 justify-between gap-4">
        <div className="w-full text-center">
          <NavLink to={locations.public.login}>
            <Button 
              type="button"
              variant="ghost"
              className="text-blue-600 hover:text-blue-700"
            >
              <FormattedText label="auth.resetPassword.change.form.back" />
            </Button>
          </NavLink>
        </div>

        <div className="w-full text-center mb-5 md:mb-0">
          <Button
            type="submit"
            className="w-full"
            disabled={!isValid || isSubmitting}
          >
            {isSubmitting ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading...
              </span>
            ) : (
              <FormattedText label="auth.resetPassword.change.form.submit" />
            )}
          </Button>
        </div>
      </div>
    </form>
  );
};